import { Fragment, createElement, useState, type ReactElement, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import * as Sentry from '@sentry/react'
import { useAuth } from '../auth/useAuth'
import { useCreatorProfile } from './useCreatorProfile'
import { packageAndUploadToy } from './toyPackaging'
import { isUploadWorking, type UploadState } from './uploadState'
import { type UploadFile } from './uploadAudio'
import { getCurrentIdentityId } from '../auth/currentIdentity'
import { useCurrentIo } from '../auth/useImpersonation'
import type { FirmwareTarget } from './fetchFirmware'
import ToyDetailsModal, {
  type ToyDetails,
} from '../components/MusicPlayer/ToyDetailsModal'
import ToyReadyModal from '../components/MusicPlayer/ToyReadyModal'
import ProfileNameModal from '../components/Profile/ProfileNameModal'
import PackagingStatus from '../components/PackagingStatus'

export type UseToyMakerOptions = {
  // Sentry attribute, e.g. 'music-player' — identifies which Make X page
  // fired the done-click metric.
  makerApp: string
  // Page-specific "do we have anything to submit?" gate. Each route
  // computes this from its own UI state (ready pads, selected episodes, …).
  hasPayload: boolean
  // Optional extra page-specific gate (e.g. encoding-in-flight, parsing).
  extraDisabled?: boolean
  firmwareTarget: FirmwareTarget
  appNameFallback: string
  // Optional pre-fill for the ToyDetailsModal — pages that have data
  // worth seeding (e.g. podcast title/description/thumbnail) can pass it
  // here. Read at the moment the modal opens; later changes don't
  // clobber whatever the user has typed.
  detailsDefaults?: ToyDetails
  // Called when the user confirms the details modal. Returns the full
  // payload (files + optional extra metadata) for packageAndUploadToy.
  buildPayload: () => Promise<{
    payloadFiles: UploadFile[]
    extraMetadata?: Record<string, unknown>
  }>
  // Optional DOM element to receive the inline packaging-status message.
  // When provided, the status is rendered into this element via a React
  // portal (instead of appended at the end of `modals`). Use the state-ref
  // pattern so the element is captured after mount:
  //
  //   const [statusEl, setStatusEl] = useState<HTMLElement | null>(null)
  //   const toyMaker = useToyMaker({ ..., statusContainer: statusEl })
  //   ...
  //   <div ref={setStatusEl} />
  statusContainer?: HTMLElement | null
}

export type UseToyMakerResult = {
  // All gates passed; the Done button can be enabled.
  canSubmit: boolean
  // Upload pipeline is currently busy (idle/collecting/preparing/uploading).
  isWorking: boolean
  // Raw upload state — exposed so routes that render inline status (ABC's
  // `tts` phase) can read it. Most routes only need `canSubmit`/`isWorking`.
  upload: UploadState
  // Setter — exposed for routes that interleave their own phases (e.g.
  // ABC's `tts`) into the upload state union.
  setUpload: (s: UploadState) => void
  // Wire to the Done button's onClick. Fires the Sentry metric and opens
  // the details modal if `canSubmit` is true.
  onDoneClick: () => void
  // Render this anywhere in your component tree. Contains all 4 wired
  // modals + the inline packaging status.
  modals: ReactElement
}

export function useToyMaker(opts: UseToyMakerOptions): UseToyMakerResult {
  const { state: authState } = useAuth()
  const io = useCurrentIo()
  const {
    profile,
    creatorName,
    profileLoaded,
    profileSaving,
    profileError,
    needsProfileName,
    saveCreatorName,
  } = useCreatorProfile()
  const [upload, setUpload] = useState<UploadState>({ status: 'idle' })

  const isWorking = isUploadWorking(upload)
  const canSubmit =
    opts.hasPayload &&
    !opts.extraDisabled &&
    !isWorking &&
    authState.status === 'authenticated' &&
    profileLoaded &&
    !!creatorName

  const onDoneClick = () => {
    Sentry.metrics.count('toy_maker.done_click', 1, {
      attributes: { makerApp: opts.makerApp },
    })
    if (!canSubmit) return
    setUpload({ status: 'collecting' })
  }

  const handleDetailsCancel = () => {
    if (upload.status === 'collecting') setUpload({ status: 'idle' })
  }

  const handleDetailsSubmit = async (details: ToyDetails) => {
    if (authState.status !== 'authenticated') return
    if (!opts.hasPayload) return
    if (!io) return

    const creatorId = getCurrentIdentityId()

    try {
      const { payloadFiles, extraMetadata } = await opts.buildPayload()
      await packageAndUploadToy({
        io,
        creatorId,
        creatorName,
        details,
        payloadFiles,
        firmwareTarget: opts.firmwareTarget,
        appNameFallback: opts.appNameFallback,
        extraMetadata,
        onState: setUpload,
      })
    } catch (err) {
      console.error(`[useToyMaker:${opts.makerApp}] packaging failed`, err)
      const message = err instanceof Error ? err.message : 'Upload failed'
      setUpload({ status: 'error', message })
    }
  }

  const modalChildren: ReactNode[] = [
    createElement(ProfileNameModal, {
      key: 'profile',
      open: needsProfileName,
      initialName: profile?.creatorName ?? '',
      saving: profileSaving,
      error: profileError,
      onSubmit: saveCreatorName,
    }),
    createElement(ToyDetailsModal, {
      key: 'details',
      open: upload.status === 'collecting',
      initialValues: opts.detailsDefaults,
      onSubmit: handleDetailsSubmit,
      onCancel: handleDetailsCancel,
    }),
    createElement(ToyReadyModal, { key: 'ready', open: upload.status === 'done' }),
  ]
  const packagingStatus = createElement(PackagingStatus, {
    key: 'status',
    state: upload,
  })
  if (opts.statusContainer) {
    modalChildren.push(createPortal(packagingStatus, opts.statusContainer))
  } else {
    modalChildren.push(packagingStatus)
  }
  const modals = createElement(Fragment, null, ...modalChildren)

  return {
    canSubmit,
    isWorking,
    upload,
    setUpload,
    onDoneClick,
    modals,
  }
}
